// Forecasting tab — predictions, restock urgency, alerts.
import { useEffect, useState } from "react";
import { getForecasting } from "@/lib/analytics";
import { fmtInt, fmtMoney, KpiCard } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import { TrendingUp, AlertTriangle, Calendar, Package, Bell } from "lucide-react";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E" };

export default function ForecastingTab() {
  const [data, setData] = useState(null);
  useEffect(() => { getForecasting().then(setData); }, []);
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const f = data.forecast;

  return (
    <div className="space-y-6" data-testid="analytics-forecasting">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Venit estimat 7z" value={f.revenue_next_7} format="money" icon={Calendar} color={COLOR.emerald} />
        <KpiCard label="Profit estimat 7z" value={f.profit_next_7} format="money" icon={TrendingUp} color={COLOR.amber} />
        <KpiCard label="Venit estimat 30z" value={f.revenue_next_30} format="money" icon={Calendar} color={COLOR.primary} />
        <KpiCard label="Profit estimat 30z" value={f.profit_next_30} format="money" icon={TrendingUp} color={COLOR.amber} />
        <KpiCard label="Venit/zi (medie)" value={f.avg_daily_revenue} format="money" color={COLOR.emerald} />
        <KpiCard label="Profit/zi (medie)" value={f.avg_daily_profit} format="money" color={COLOR.amber} />
      </div>

      {/* Alerts panel */}
      <Card title={<><Bell className="h-3.5 w-3.5 inline mr-1" /> Alerte ({data.alerts.length})</>}>
        {data.alerts.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-8">Nicio alertă activă</div>
        ) : (
          <ul className="space-y-1.5 max-h-72 overflow-auto">
            {data.alerts.slice(0, 30).map((a, i) => (
              <li
                key={i}
                className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 ${
                  a.level === "danger" ? "bg-rose-500/10 border border-rose-500/30 text-rose-200"
                  : "bg-amber-500/10 border border-amber-500/30 text-amber-200"
                }`}
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="flex-1">{a.message}</span>
                <span className="text-[10px] uppercase text-muted-foreground">{a.type}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={<><Package className="h-3.5 w-3.5 inline mr-1" /> Restock urgent ({data.restock_now.length})</>}>
          <RestockTable rows={data.restock_now} urgent />
        </Card>
        <Card title={<><Package className="h-3.5 w-3.5 inline mr-1" /> Restock în curând ({data.out_of_stock_soon.length})</>}>
          <RestockTable rows={data.out_of_stock_soon} />
        </Card>
      </div>
    </div>
  );
}

function RestockTable({ rows, urgent = false }) {
  if (!rows.length) return <div className="text-center text-xs text-muted-foreground py-8">Niciun produs în această categorie</div>;
  return (
    <div className="max-h-[420px] overflow-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase text-muted-foreground sticky top-0 bg-card">
          <tr>
            <th className="text-left py-2 px-2">Produs</th>
            <th className="text-right">Stoc</th>
            <th className="text-right">Velocity/zi</th>
            <th className="text-right">Zile rămase</th>
            <th className="text-right pr-2">Comandă sugerată</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.variant_id} className="border-t border-border/50 hover:bg-secondary/20">
              <td className="py-1.5 px-2">
                <div className="flex items-center gap-2 max-w-sm">
                  {r.image ? <img src={r.image} alt="" className="h-7 w-7 rounded-md object-cover bg-secondary/40" /> : <div className="h-7 w-7 rounded-md bg-secondary/40" />}
                  <div className="truncate">
                    <div className="text-sm font-medium truncate" title={r.title}>{r.title}</div>
                    <div className="text-[10px] text-muted-foreground">{r.sku} · {r.supplier_name || "Fără furnizor"}</div>
                  </div>
                </div>
              </td>
              <td className="text-right tabular-nums">{fmtInt(r.warehouse_qty)}</td>
              <td className="text-right tabular-nums">{r.velocity_per_day}</td>
              <td className={`text-right tabular-nums font-semibold ${urgent ? "text-rose-500" : "text-amber-500"}`}>
                {r.days_remaining === 0 ? "Epuizat" : (r.days_remaining ?? "—")}
              </td>
              <td className="text-right tabular-nums font-bold pr-2 text-primary">{fmtInt(r.suggested_qty)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
